(function() {
  'use strict';

  const container = document.getElementById('chain-configurator');
  if (!container) return;

  // ---- Config ----
  // Fallback: ensure correct collection handles even if section settings are stale
  const CHAIN_HANDLE = 'collection-bracelet-chaine';
  const CHARM_HANDLE = 'collection-charm-chaine';
  const config = {
    chainCollection: container.dataset.chainCollection === CHAIN_HANDLE ? CHAIN_HANDLE : CHAIN_HANDLE,
    charmCollection: container.dataset.charmCollection === CHARM_HANDLE ? CHARM_HANDLE : CHARM_HANDLE,
    maxCharms: parseInt(container.dataset.maxCharms) || 7,
  };

  // ---- i18n ----
  const i18n = {};
  Object.keys(container.dataset).forEach(key => {
    if (key.startsWith('i18n')) {
      const cleanKey = key.replace('i18n', '').replace(/^[A-Z]/, c => c.toLowerCase());
      i18n[cleanKey] = container.dataset[key];
    }
  });

  // ---- State ----
  let currentStep = 1;
  let selectedChain = null;
  let selectedCharms = [];
  let chains = [];
  let charms = [];
  let activeCategory = 'all';
  let searchQuery = '';

  // ---- Canvas ----
  const canvas = document.getElementById('chain-preview-canvas');
  const ctx = canvas.getContext('2d');
  const canvasSize = 600;
  canvas.width = canvasSize;
  canvas.height = canvasSize;

  // Charm positions: charms hang OUTSIDE the chain
  // Positioned on the lower 3/4 of the bracelet circle
  // Each charm hangs outward from the chain
  // 7 slots à distance égale sur un arc de 270° (3/4 bas du cercle)
  // Centre chaîne: (0.475, 0.51), rayon: 0.26
  // Orientation perpendiculaire au cercle (charm pointe vers l'extérieur)
  // Ordre d'affichage: bas, puis alternance droite/gauche en montant
  const charmSlots = [
    { x: 0.47, y: 0.77, angle: 0 },      // 1: bottom (centre)
    { x: 0.66, y: 0.69, angle: -45 },    // 2: lower-right (droite du 1)
    { x: 0.29, y: 0.69, angle: 45 },     // 3: lower-left (gauche du 1)
    { x: 0.73, y: 0.51, angle: -90 },    // 4: right (droite du 2)
    { x: 0.21, y: 0.51, angle: 90 },     // 5: left (gauche du 3)
    { x: 0.66, y: 0.33, angle: -135 },   // 6: upper-right (droite du 4)
    { x: 0.29, y: 0.33, angle: 135 },    // 7: upper-left (gauche du 5)
  ];

  // Image cache
  const imageCache = {};

  function loadImage(src) {
    if (imageCache[src]) return Promise.resolve(imageCache[src]);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { imageCache[src] = img; resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }

  function getChainViewImageUrl(product) {
    // Chain canvas preview: use the FIRST image (clean product shot)
    return product.images[0]?.src;
  }

  function getCharmViewImageUrl(product) {
    // Charm canvas preview: use the LAST image (normalized PNG with correct proportions)
    if (product.images.length > 1) {
      return product.images[product.images.length - 1].src;
    }
    return product.images[0]?.src;
  }

  function getChainThumbUrl(product) {
    // Chain cards: use the LAST image (zoom on chain links)
    if (product.images.length > 1) {
      return product.images[product.images.length - 1].src;
    }
    return product.images[0]?.src;
  }

  function getCharmThumbUrl(product) {
    // Charm cards: use the FIRST image (detoured charm on white/transparent bg)
    return product.images[0]?.src;
  }

  function getCharmDimensions(product) {
    // Extract dimensions from body_html: "Dimensions : 1.2cm × 1.2cm (diamètre 12mm)"
    const body = product.body_html || '';
    const match = body.match(/Dimensions\s*:\s*<\/strong>\s*([^<]+)/i);
    if (match) {
      // Return just "1.2 × 1.2 cm"
      let dims = match[1].trim();
      // Remove the diameter part in parentheses
      dims = dims.replace(/\s*\([^)]*\)/, '').trim();
      return dims;
    }
    return null;
  }

  function getCharmShortName(product) {
    // Extract short name: "Charm pendant [NOM], pour femme, modèle X" → "Nom"
    const title = product.title || '';
    const lower = title.toLowerCase();
    let start = lower.indexOf('pendant ');
    if (start !== -1) {
      start += 'pendant '.length;
    } else {
      start = lower.indexOf('charm ') !== -1 ? lower.indexOf('charm ') + 'charm '.length : 0;
    }
    let end = lower.indexOf(', pour');
    if (end === -1) end = lower.indexOf(', modèle');
    if (end === -1) end = title.length;
    let short = title.substring(start, end).trim();
    // Capitalize first letter
    if (short.length > 0) {
      short = short.charAt(0).toUpperCase() + short.slice(1);
    }
    return short || title;
  }

  function resizeImageUrl(url, size) {
    if (!url) return '';
    // Shopify CDN resize
    if (url.includes('cdn.shopify.com')) {
      const separator = url.includes('?') ? '&' : '?';
      return url + separator + 'width=' + size;
    }
    return url;
  }

  // ---- Canvas Rendering ----
  async function renderCanvas() {
    ctx.clearRect(0, 0, canvasSize, canvasSize);
    
    // Draw background
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    // Show marketing placeholder when nothing is selected
    if (!selectedChain && selectedCharms.length === 0) {
      const cx = canvasSize / 2;
      ctx.textAlign = 'center';
      
      // Trustpilot logo - first, with multiply blend to hide white background
      const trustpilotUrl = 'https://cdn.shopify.com/s/files/1/0813/5724/2716/files/lbdb-trustpilot-checkout-2_6256900b-026c-41bb-825c-e7e684fefc33.jpg?v=1768557173';
      try {
        const tpImg = await loadImage(trustpilotUrl);
        const tpWidth = canvasSize * 0.50;
        const tpHeight = tpWidth * (tpImg.height / tpImg.width);
        ctx.globalCompositeOperation = 'multiply';
        ctx.drawImage(tpImg, cx - tpWidth / 2, canvasSize * 0.08, tpWidth, tpHeight);
        ctx.globalCompositeOperation = 'source-over';
      } catch(e) {
        console.warn('Failed to load Trustpilot logo', e);
      }
      
      // Title - big
      ctx.fillStyle = '#1a1a2e';
      ctx.font = `bold ${canvasSize * 0.065}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillText('Cr\u00e9ez votre bracelet', cx, canvasSize * 0.38);
      ctx.fillText('unique', cx, canvasSize * 0.46);
      
      // Subtitle - dynamic counts
      const chainCount = chains.length;
      const charmCount = charms.length;
      ctx.fillStyle = '#555';
      ctx.font = `${canvasSize * 0.038}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillText(`${chainCount} bracelets et plus de ${charmCount} charms`, cx, canvasSize * 0.56);
      ctx.fillText('\u00e0 choisir', cx, canvasSize * 0.62);
      
      // Arrow pointing down
      ctx.fillStyle = '#1a1a2e';
      ctx.font = `${canvasSize * 0.07}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillText('\u2193', cx, canvasSize * 0.76);
      ctx.font = `${canvasSize * 0.032}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillStyle = '#888';
      ctx.fillText('Commencez ci-dessous', cx, canvasSize * 0.82);
      
      ctx.textAlign = 'left';
      return;
    }

    // Draw chain
    if (selectedChain) {
      const chainViewUrl = getChainViewImageUrl(selectedChain);
      if (chainViewUrl) {
        try {
          const chainImg = await loadImage(resizeImageUrl(chainViewUrl, 600));
          ctx.drawImage(chainImg, 0, 0, canvasSize, canvasSize);
        } catch(e) {
          console.warn('Failed to load chain view image', e);
        }
      }
    }

    // Draw charms hanging from the chain
    // Charm images are 500x500 PNG with transparent background
    // The ring/hook is positioned at top-center (x=250, y=40) in the normalized images
    for (let i = 0; i < selectedCharms.length; i++) {
      const charm = selectedCharms[i];
      const slot = charmSlots[i];
      const charmViewUrl = getCharmViewImageUrl(charm);
      
      if (charmViewUrl && slot) {
        try {
          const charmImg = await loadImage(resizeImageUrl(charmViewUrl, 300));
          
          // Draw charm as a pendant hanging from the chain
          // Images are 500x500 PNG transparent, ring at top-center (x=250, y=40)
          const charmSize = canvasSize * 0.28; // ~168px on a 600px canvas
          
          // Position: slot defines where the charm's ring attaches to the chain
          const pivotX = slot.x * canvasSize;
          const pivotY = slot.y * canvasSize;
          
          // Apply slight rotation for natural hanging look
          const angleRad = (slot.angle || 0) * Math.PI / 180;
          
          ctx.save();
          ctx.translate(pivotX, pivotY);
          ctx.rotate(angleRad);
          
          // The ring is at 8% from top of image (40/500)
          // We want the ring to be exactly at the pivot point (0,0 after translate)
          // So we draw the image starting at y = -ringOffset (ring aligns with pivot)
          const ringOffset = charmSize * 0.08;
          ctx.drawImage(
            charmImg,
            -charmSize / 2, -ringOffset, charmSize, charmSize
          );
          
          ctx.restore();
        } catch(e) {
          console.warn('Failed to load charm view image', e);
        }
      }
    }

    // Update charm count badge
    const badge = container.querySelector('.chain-configurator__charm-count');
    if (badge) {
      badge.textContent = selectedCharms.length + '/' + config.maxCharms;
    }
  }

  // ---- Data Loading ----
  async function fetchCollection(handle) {
    const products = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
      const url = `/collections/${handle}/products.json?limit=250&page=${page}`;
      try {
        const resp = await fetch(url);
        if (!resp.ok) break;
        const data = await resp.json();
        if (data.products && data.products.length > 0) {
          products.push(...data.products);
          page++;
          if (data.products.length < 250) hasMore = false;
        } else {
          hasMore = false;
        }
      } catch(e) {
        console.error('Error fetching collection', handle, e);
        hasMore = false;
      }
    }
    return products;
  }

  // ---- Categories ----
  function getTags(product) {
    if (!product.tags) return [];
    if (Array.isArray(product.tags)) return product.tags;
    return product.tags.split(', ');
  }

  // Tags to exclude from category filters (technical/system tags)
  const EXCLUDED_TAGS = [
    'Collection Charm Chaîne',
    'Collection Charm',
    'Bracelets chaîne personnalisables',
    'Acier Inoxydable',
    'Pour Femme',
    'Pour Homme',
    'Waterproof',
    'post_studio',
    'post_video'
  ];

  function getCharmCategories(charms) {
    const categories = new Set();
    charms.forEach(charm => {
      const tags = getTags(charm);
      tags.forEach(tag => {
        if (!EXCLUDED_TAGS.includes(tag)) {
          categories.add(tag);
        }
      });
    });
    return Array.from(categories).sort();
  }

  // ---- Rendering UI ----
  function renderChains() {
    const grid = document.getElementById('chains-grid');
    grid.innerHTML = '';
    
    chains.forEach(chain => {
      const card = document.createElement('div');
      card.className = 'chain-configurator__chain-card';
      if (selectedChain && selectedChain.id === chain.id) {
        card.classList.add('chain-configurator__chain-card--selected');
      }
      
      const thumbUrl = getChainThumbUrl(chain);
      const name = chain.title.replace(/Bracelet chaîne personnalisable, modèle\s*/i, '');
      const price = parseFloat(chain.variants[0]?.price || 0);
      
      card.innerHTML = `
        <img src="${resizeImageUrl(thumbUrl, 300)}" alt="${name}" loading="lazy" />
        <p class="chain-configurator__chain-name">${name}</p>
        <p class="chain-configurator__chain-price">${formatPrice(price)}</p>
      `;
      
      card.addEventListener('click', () => {
        selectedChain = chain;
        renderChains();
        renderCanvas();
        updateButtons();
        updatePrice();
      });
      
      grid.appendChild(card);
    });
  }

  function renderCharmCategories() {
    const categoriesContainer = document.getElementById('charm-categories');
    const categories = getCharmCategories(charms);
    
    let html = `<button class="chain-configurator__category-btn chain-configurator__category-btn--active" data-category="all">${i18n.all || 'Tous'}</button>`;
    categories.forEach(cat => {
      html += `<button class="chain-configurator__category-btn" data-category="${cat}">${cat}</button>`;
    });
    
    categoriesContainer.innerHTML = html;
    
    categoriesContainer.querySelectorAll('.chain-configurator__category-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        activeCategory = btn.dataset.category;
        categoriesContainer.querySelectorAll('.chain-configurator__category-btn').forEach(b => b.classList.remove('chain-configurator__category-btn--active'));
        btn.classList.add('chain-configurator__category-btn--active');
        renderCharms();
      });
    });
  }

  function renderCharms() {
    const grid = document.getElementById('charms-grid');
    const noResults = document.getElementById('no-results');
    
    let filteredCharms = charms;
    
    // Filter by category
    if (activeCategory !== 'all') {
      filteredCharms = filteredCharms.filter(charm => {
        const tags = getTags(charm);
        return tags.includes(activeCategory);
      });
    }
    
    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filteredCharms = filteredCharms.filter(charm => 
        charm.title.toLowerCase().includes(q)
      );
    }
    
    if (filteredCharms.length === 0) {
      grid.innerHTML = '';
      noResults.style.display = 'block';
      return;
    }
    
    noResults.style.display = 'none';
    grid.innerHTML = '';
    
    filteredCharms.forEach(charm => {
      const card = document.createElement('div');
      card.className = 'chain-configurator__charm-card';
      
      const countInSelection = selectedCharms.filter(c => c.id === charm.id).length;
      const isMaxReached = selectedCharms.length >= config.maxCharms;
      
      if (countInSelection > 0) card.classList.add('chain-configurator__charm-card--selected');
      if (isMaxReached && countInSelection === 0) card.classList.add('chain-configurator__charm-card--disabled');
      
      const thumbUrl = getCharmThumbUrl(charm);
      const name = getCharmShortName(charm);
      const price = parseFloat(charm.variants[0]?.price || 0);
      const dims = getCharmDimensions(charm);
      
      card.innerHTML = `
        <img src="${resizeImageUrl(thumbUrl, 200)}" alt="${name}" loading="lazy" />
        <p class="chain-configurator__charm-name">${name}</p>
        ${dims ? `<p class="chain-configurator__charm-dims">${dims}</p>` : ''}
        <p class="chain-configurator__charm-price">${formatPrice(price)}</p>
        ${countInSelection > 0 ? `<span class="chain-configurator__charm-count-badge">${countInSelection}</span>` : ''}
        ${countInSelection > 0 ? `<button class="chain-configurator__charm-remove-btn" data-charm-id="${charm.id}" title="Supprimer"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14M10 11v6M14 11v6"/></svg></button>` : ''}
      `;
      
      // Click on card = add charm
      card.addEventListener('click', (e) => {
        if (e.target.closest('.chain-configurator__charm-remove-btn')) return;
        if (!isMaxReached) {
          selectedCharms.push(charm);
          renderCharms();
          renderCanvas();
          updateSelectedBar();
          updateButtons();
          updatePrice();
        }
      });
      
      // Click on trash icon = remove one instance
      const removeBtn = card.querySelector('.chain-configurator__charm-remove-btn');
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = selectedCharms.findIndex(c => c.id === charm.id);
          if (idx !== -1) {
            selectedCharms.splice(idx, 1);
            renderCharms();
            renderCanvas();
            updateSelectedBar();
            updateButtons();
            updatePrice();
          }
        });
      }
      
      grid.appendChild(card);
    });
  }

  function renderSummary() {
    const summaryEl = document.getElementById('summary-content');
    let html = '';
    
    const promo = calculatePromo();
    
    promo.items.forEach((item, index) => {
      const isChain = item.role === 'base';
      const name = isChain 
        ? item.product.title.replace(/Bracelet chaîne personnalisable, modèle\s*/i, '')
        : item.product.title.replace(/Charm\s*/i, '').replace(/, pour bracelet chaîne personnalisable/i, '');
      const thumb = isChain ? getChainThumbUrl(item.product) : getCharmThumbUrl(item.product);
      const type = isChain ? (i18n.chain || 'Chaîne') : (i18n.charm || 'Charm');
      
      const hasDiscount = item.discountLabel !== null;
      let priceHtml = '';
      if (hasDiscount) {
        priceHtml += `<span class="chain-configurator__price-original">${formatPrice(item.originalPrice)}</span> `;
        priceHtml += `<span class="chain-configurator__price-discounted">${formatPrice(item.discountedPrice)}</span>`;
        priceHtml += ` <span class="chain-configurator__price-badge">${item.discountLabel}</span>`;
      } else {
        priceHtml = formatPrice(item.originalPrice);
      }
      
      html += `
        <div class="chain-configurator__summary-item">
          <img src="${resizeImageUrl(thumb, 100)}" alt="${name}" />
          <div class="chain-configurator__summary-item-info">
            <div class="chain-configurator__summary-item-name">${name}</div>
            <div class="chain-configurator__summary-item-type">${type}</div>
          </div>
          <div class="chain-configurator__summary-item-price">${priceHtml}</div>
          ${!isChain ? `<button class="chain-configurator__summary-item-remove" data-charm-index="${index - 1}" title="Supprimer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14M10 11v6M14 11v6"/></svg>
          </button>` : ''}
        </div>
      `;
    });
    
    summaryEl.innerHTML = html;
    
    // Bind remove buttons (by index to handle duplicates)
    summaryEl.querySelectorAll('.chain-configurator__summary-item-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const charmIndex = parseInt(btn.dataset.charmIndex);
        selectedCharms.splice(charmIndex, 1);
        renderSummary();
        renderCanvas();
        updatePrice();
        updateButtons();
      });
    });
  }

  // ---- Helpers ----
  function formatPrice(price) {
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  function updateSelectedBar() {
    const bar = document.getElementById('selected-bar');
    const count = document.getElementById('selected-count');
    if (selectedCharms.length > 0) {
      bar.style.display = 'block';
      count.textContent = selectedCharms.length;
    } else {
      bar.style.display = 'none';
    }
  }

  /* --- Promo Evergreen Logic ---
     Shopify has 2 active BXGY automatic discounts:
     - "2ème à -50%": Buy 1, Get 1 at 50% off (applies per pair, cheapest item discounted)
     - "3ème OFFERT": Buy 2, Get 1 Free (applies per lot of 3, cheapest item free)
     Both have usesPerOrderLimit = null (unlimited applications).
     Shopify applies the BEST offer for the customer (highest total savings).
     Items are sorted by price DESC; the cheapest in each group gets the discount.
  */
  function calculatePromo() {
    if (!selectedChain) return { items: [], totalBefore: 0, totalAfter: 0, savings: 0 };

    const allItems = [];
    allItems.push({
      product: selectedChain,
      role: 'base',
      originalPrice: parseFloat(selectedChain.variants[0]?.price || 0)
    });
    selectedCharms.forEach((charm) => {
      allItems.push({
        product: charm,
        role: 'charm',
        originalPrice: parseFloat(charm.variants[0]?.price || 0)
      });
    });

    // Sort items by price descending for discount calculation
    const sorted = [...allItems].sort((a, b) => b.originalPrice - a.originalPrice);

    // --- Offer A: "2ème à -50%" (Buy 1 Get 1 at 50% off) ---
    const offerA = { savings: 0, discounts: new Map() };
    for (let i = 0; i < Math.floor(sorted.length / 2); i++) {
      const discountedItem = sorted[i * 2 + 1];
      const saving = discountedItem.originalPrice * 0.5;
      offerA.savings += saving;
      offerA.discounts.set(discountedItem, { price: discountedItem.originalPrice * 0.5, label: '-50%' });
    }

    // --- Offer B: "3ème OFFERT" (Buy 2 Get 1 Free) ---
    const offerB = { savings: 0, discounts: new Map() };
    for (let i = 0; i < Math.floor(sorted.length / 3); i++) {
      const discountedItem = sorted[i * 3 + 2];
      const saving = discountedItem.originalPrice;
      offerB.savings += saving;
      offerB.discounts.set(discountedItem, { price: 0, label: i18n.free || 'OFFERT' });
    }

    // Choose the best offer for the customer
    const bestOffer = (offerB.savings >= offerA.savings) ? offerB : offerA;

    allItems.forEach(item => {
      item.discountedPrice = item.originalPrice;
      item.discountLabel = null;
      const disc = bestOffer.discounts.get(item);
      if (disc) {
        item.discountedPrice = disc.price;
        item.discountLabel = disc.label;
      }
    });

    const totalBefore = allItems.reduce((sum, i) => sum + i.originalPrice, 0);
    const totalAfter = allItems.reduce((sum, i) => sum + i.discountedPrice, 0);

    return {
      items: allItems,
      totalBefore,
      totalAfter,
      savings: totalBefore - totalAfter
    };
  }

  function updatePrice() {
    const priceEl = container.querySelector('.chain-configurator__price-value');
    const priceBeforeEl = document.getElementById('chain-price-before');
    const promoBanner = document.getElementById('chain-promo-banner');
    
    const promo = calculatePromo();
    
    priceEl.textContent = formatPrice(promo.totalAfter);
    
    // Show/hide price before (barré)
    if (promo.savings > 0 && priceBeforeEl) {
      priceBeforeEl.style.display = 'inline';
      priceBeforeEl.textContent = formatPrice(promo.totalBefore);
    } else if (priceBeforeEl) {
      priceBeforeEl.style.display = 'none';
    }
    
    // Show/hide promo banner
    if (promo.savings > 0 && promoBanner) {
      promoBanner.style.display = 'flex';
      const savingsText = (i18n.savings || 'Vous économisez <strong>{{ amount }}</strong> !')
        .replace('{{ amount }}', formatPrice(promo.savings));
      promoBanner.innerHTML = `<span class="chain-configurator__promo-icon">\uD83C\uDF89</span><span>${savingsText}</span>`;
    } else if (promoBanner) {
      promoBanner.style.display = 'none';
    }
  }

  function updateButtons() {
    const btnBack = document.getElementById('btn-back');
    const btnNext = document.getElementById('btn-next');
    
    btnBack.style.display = currentStep > 1 ? 'block' : 'none';
    
    if (currentStep === 1) {
      btnNext.disabled = !selectedChain;
      btnNext.textContent = i18n.continue || 'Continuer';
    } else if (currentStep === 2) {
      btnNext.disabled = selectedCharms.length === 0;
      btnNext.textContent = i18n.validate || 'Valider mes charms';
    } else if (currentStep === 3) {
      btnNext.disabled = false;
      btnNext.textContent = i18n.addToCart || 'Ajouter au panier';
    }
  }

  function goToStep(step) {
    currentStep = step;
    
    // Update panels
    container.querySelectorAll('.chain-configurator__panel').forEach(panel => {
      panel.classList.remove('chain-configurator__panel--active');
    });
    container.querySelector(`[data-panel="${step}"]`).classList.add('chain-configurator__panel--active');
    
    // Update stepper
    container.querySelectorAll('.chain-configurator__step').forEach(stepEl => {
      const stepNum = parseInt(stepEl.dataset.step);
      stepEl.classList.remove('chain-configurator__step--active', 'chain-configurator__step--done');
      if (stepNum === step) stepEl.classList.add('chain-configurator__step--active');
      else if (stepNum < step) stepEl.classList.add('chain-configurator__step--done');
    });
    
    // Render step content
    if (step === 3) renderSummary();
    
    updateButtons();
    
    // Scroll to top of content
    container.querySelector('.chain-configurator__content').scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ---- Add to Cart (with cart drawer) ----
  async function addToCart() {
    const btnNext = document.getElementById('btn-next');
    btnNext.disabled = true;
    btnNext.textContent = i18n.adding || 'Ajout en cours...';
    
    const chainName = selectedChain ? selectedChain.title.replace(/Bracelet chaîne personnalisable, modèle\s*/i, '') : '';
    const items = [];
    
    // Add chain
    if (selectedChain) {
      items.push({
        id: selectedChain.variants[0].id,
        quantity: 1,
        properties: {
          '_bundle': 'compose-ton-bracelet-chaine',
          '_bundle_role': 'base',
          'Composition': `Base + ${selectedCharms.length} charm(s)`
        }
      });
    }
    
    // Add charms
    selectedCharms.forEach((charm, i) => {
      items.push({
        id: charm.variants[0].id,
        quantity: 1,
        properties: {
          '_bundle': 'compose-ton-bracelet-chaine',
          '_bundle_role': 'charm',
          '_bundle_position': (i + 1).toString(),
          'Pour bracelet': chainName
        }
      });
    });
    
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (resp.ok) {
        // Refresh and open the cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show();
          }
        }, 300);
      } else {
        let errorMsg = "Impossible d'ajouter au panier";
        try {
          const error = await resp.json();
          errorMsg = error.description || errorMsg;
        } catch (e) { /* ignore */ }
        console.error('Add to cart error:', errorMsg);
        btnNext.disabled = false;
        btnNext.textContent = i18n.addToCart || 'Ajouter au panier';
      }
    } catch(e) {
      console.error('Add to cart error:', e);
      btnNext.disabled = false;
      btnNext.textContent = i18n.addToCart || 'Ajouter au panier';
    }
  }

  // ---- Event Listeners ----
  document.getElementById('btn-next').addEventListener('click', () => {
    if (currentStep === 3) {
      addToCart();
    } else {
      goToStep(currentStep + 1);
    }
  });

  document.getElementById('btn-back').addEventListener('click', () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  });

  // Search
  document.getElementById('charm-search').addEventListener('input', (e) => {
    searchQuery = e.target.value;
    renderCharms();
  });

  // ---- Init ----
  async function init() {
    // Show loading state
    const chainsGrid = document.getElementById('chains-grid');
    chainsGrid.innerHTML = '<div class="chain-configurator__shimmer" style="height:150px;grid-column:1/-1;"></div>';
    
    // Fetch data
    const [chainsData, charmsData] = await Promise.all([
      fetchCollection(config.chainCollection),
      fetchCollection(config.charmCollection)
    ]);
    
    chains = chainsData;
    charms = charmsData;
    
    // Render
    renderChains();
    renderCharmCategories();
    renderCharms();
    renderCanvas();
    updateButtons();
    updatePrice();
  }

  init();
})();
